// Tipos e formatação de Compras. Quantidade chega como texto decimal com 3 casas ("12.500"), igual ao Estoque;
// dinheiro em centavos (texto). Nenhum cálculo de quantidade ou dinheiro passa por float.

export type StatusDoc = 'rascunho' | 'enviado' | 'confirmado' | 'cancelado';
export type StatusPedido = 'rascunho' | 'enviado' | 'confirmado' | 'parcialmente_recebido' | 'recebido' | 'encerrado' | 'cancelado';
export const STATUS_DOC: StatusDoc[] = ['rascunho', 'enviado', 'confirmado', 'cancelado'];
export const STATUS_PEDIDO: StatusPedido[] = ['rascunho', 'enviado', 'confirmado', 'parcialmente_recebido', 'recebido', 'encerrado', 'cancelado'];

export interface ItemNomeado {
  n: number;
  produto_id: string;
  variacao_id: string | null;
  produto: string;
  produto_codigo: string;
  unidade: string;
  variacao: string | null;
  quantidade: string;
}

export interface Requisicao {
  id: string;
  numero: string;
  descricao: string;
  necessidade: string | null;
  status: StatusDoc;
  itens: number;
  cotacoes: number;
  menor_total_centavos: string | null;
  pedido_id: string | null;
  pedido_numero: string | null;
  created_at: string;
}

export interface CotacaoResumo {
  id: string;
  numero: string;
  requisicao_id: string;
  requisicao_numero: string;
  requisicao_descricao: string;
  requisicao_status: StatusDoc;
  fornecedor_id: string;
  fornecedor_nome: string;
  prazo_entrega_dias: number;
  validade: string | null;
  total_centavos: string;
  moeda: string;
  status: StatusDoc;
  itens: number;
  menor: boolean;
  pedido_id: string | null;
}

export interface FichaRequisicao {
  id: string;
  numero: string;
  descricao: string;
  necessidade: string | null;
  status: StatusDoc;
  created_at: string;
  itens: ItemNomeado[];
  cotacoes: { id: string; numero: string; fornecedor_id: string; fornecedor_nome: string; prazo_entrega_dias: number; validade: string | null; total_centavos: string; moeda: string; status: StatusDoc; pedido_id: string | null }[];
}

export interface PedidoResumo {
  id: string;
  numero: string;
  fornecedor_id: string;
  fornecedor_nome: string;
  empresa_id: string | null;
  empresa_nome: string | null;
  status: StatusPedido;
  moeda: string;
  total_centavos: string;
  recebido_centavos: string;
  condicao_pagamento_dias: number;
  previsao_entrega: string | null;
  itens: number;
  atrasado: boolean;
}

export interface FichaPedido {
  id: string;
  numero: string;
  fornecedor_id: string;
  fornecedor_nome: string;
  deposito_id: string;
  deposito: string;
  requisicao_id: string | null;
  requisicao_numero: string | null;
  cotacao_id: string | null;
  cotacao_numero: string | null;
  empresa_id: string | null;
  empresa_nome: string | null;
  condicao_pagamento_dias: number;
  prazo_entrega_dias: number;
  /** emissão do pedido (AAAA-MM-DD); o recebimento não pode ser anterior */
  emissao: string | null;
  previsao_entrega: string | null;
  moeda: string;
  total_centavos: string;
  recebido_centavos: string;
  observacao: string | null;
  status: StatusPedido;
  created_at: string;
  /** SPEC2-04 quando o saldo foi encerrado */
  encerrado_em: string | null;
  motivo_encerramento: string | null;
  itens: (ItemNomeado & { id: string; recebido: string; quantidade_cancelada: string; saldo: string; preco_unitario_centavos: string; valor_centavos: string; controla_lote: boolean })[];
  recebimentos: { id: string; numero: string; data: string; nota_fiscal: string | null; total_centavos: string; vencimento: string; documento_id: string | null; documento_numero: string | null; completo: boolean }[];
}

export interface RecebimentoResumo {
  id: string;
  numero: string;
  pedido_id: string;
  pedido_numero: string;
  fornecedor_nome: string;
  data: string;
  nota_fiscal: string | null;
  total_centavos: string;
  moeda: string;
  vencimento: string;
  documento_id: string | null;
  documento_numero: string | null;
  completo: boolean;
  /** SM2-REV-01 alguma linha recebida com custo diferente do preço do pedido (confirmado por quem recebeu). */
  custo_divergente: boolean;
  itens: number;
}

export interface FichaRecebimento extends Omit<RecebimentoResumo, 'itens'> {
  fornecedor_id: string;
  deposito: string;
  observacao: string | null;
  itens: (ItemNomeado & { custo_unitario_centavos: string; preco_pedido_centavos: string | null; valor_centavos: string; lote: string | null; validade: string | null; movimentacao_id: string | null; movimentacao_numero: string | null })[];
}

export interface Fornecedor {
  parceiro_id: string;
  nome: string;
  tipo: 'pessoa' | 'empresa';
  documento: string | null;
  email: string | null;
  telefone: string | null;
  configurado: boolean;
  condicao_pagamento_dias: number;
  prazo_entrega_dias: number;
  observacao: string | null;
  pedidos_abertos: number;
  comprado_centavos: string;
  ultimo_pedido: string | null;
}

export interface Preco {
  id: string;
  produto_id: string;
  produto: string;
  produto_codigo: string;
  unidade: string;
  variacao: string | null;
  fornecedor_id: string;
  fornecedor: string;
  preco_unitario_centavos: string;
  moeda: string;
  origem: 'cotacao' | 'recebimento';
  origem_numero: string;
  data: string;
}

export interface ProdutoOpcao {
  id: string;
  codigo: string;
  nome: string;
  unidade: string;
  controla_lote: boolean;
  variacoes: number;
  ativo: boolean;
  moeda: string;
}

export function formatarDinheiro(centavos: string | null | undefined, moeda = 'BRL', idioma = 'pt-BR'): string {
  if (centavos === null || centavos === undefined || centavos === '') return '';
  const c = BigInt(centavos);
  const neg = c < 0n;
  const a = neg ? -c : c;
  const decimal = `${neg ? '-' : ''}${(a / 100n).toString()}.${(a % 100n).toString().padStart(2, '0')}`;
  return new Intl.NumberFormat(idioma, { style: 'currency', currency: moeda }).format(decimal as unknown as number);
}

const separador = (idioma: string) => new Intl.NumberFormat(idioma).formatToParts(1.5).find((p) => p.type === 'decimal')?.value ?? ',';

/** "1250.500" vira "1.250,5" em pt, sem float. */
export function formatarQtd(t: string | null | undefined, idioma: string): string {
  if (t === null || t === undefined || t === '') return '';
  const neg = t.startsWith('-');
  const [i = '0', f = ''] = t.replace('-', '').split('.');
  const dec = f.replace(/0+$/, '');
  return `${neg ? '-' : ''}${new Intl.NumberFormat(idioma).format(BigInt(i))}${dec ? `${separador(idioma)}${dec}` : ''}`;
}

/** Texto digitado ("1,5", "1.5") para o formato da API ("1.5"); null se vazio, 'invalido' se não é decimal com até 3 casas. */
export function qtdParaApi(texto: string): string | null {
  const t = texto.trim().replace(/\s/g, '');
  if (!t) return null;
  const n = t.includes(',') ? t.replace(/\./g, '').replace(',', '.') : t;
  return /^\d{1,15}(\.\d{1,3})?$/.test(n) ? n : 'invalido';
}

/** "1.500" para o campo de edição ("1,5"). */
export const qtdParaCampo = (t: string | null | undefined): string => (!t ? '' : t.includes('.') ? t.replace(/0+$/, '').replace(/\.$/, '').replace('.', ',') : t);

export function reaisParaCentavos(texto: string): string | null {
  const t = texto.trim().replace(/\s|R\$/g, '');
  if (!t) return null;
  const m = /^(\d{1,3}(?:\.\d{3})*|\d+)(?:,(\d{1,2}))?$/.exec(t);
  if (!m) return 'invalido';
  return (BigInt(m[1]!.replace(/\./g, '')) * 100n + BigInt((m[2] ?? '').padEnd(2, '0') || '0')).toString();
}

export function centavosParaCampo(c: string | null | undefined): string {
  if (!c) return '';
  const v = BigInt(c);
  return `${(v / 100n).toString()},${(v % 100n).toString().padStart(2, '0')}`;
}

/** Valor de uma linha (quantidade em texto x preço em centavos), meio para cima, sem float. */
export function valorLinha(qtd: string | null, preco: string | null): bigint {
  if (!qtd || qtd === 'invalido' || !preco || preco === 'invalido') return 0n;
  const [i = '0', f = ''] = qtd.split('.');
  const mil = BigInt(i) * 1000n + BigInt(f.padEnd(3, '0').slice(0, 3));
  return (mil * BigInt(preco) * 2n + 1000n) / 2000n;
}

/** Data civil (campo date, AAAA-MM-DD): mostra o próprio dia, sem conversão de fuso. Não usar para instantes. */
export const formatarData = (iso: string | null | undefined, idioma: string) => (iso ? new Intl.DateTimeFormat(idioma, { timeZone: 'UTC' }).format(new Date(`${iso.slice(0, 10)}T00:00:00Z`)) : '');

/**
 * VB11-01 Instante (timestamptz: created_at, encerrado_em...): o dia no fuso do navegador (ou no informado), a partir
 * de new Date(iso). Cortar a data UTC adiantava um dia depois das 21h de Brasília.
 */
export const formatarInstante = (iso: string | null | undefined, idioma: string, fuso?: string) =>
  iso ? new Intl.DateTimeFormat(idioma, { day: '2-digit', month: '2-digit', year: 'numeric', ...(fuso ? { timeZone: fuso } : {}) }).format(new Date(iso)) : '';

export const hojeIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const somarDias = (d: string, n: number): string => new Date(Date.parse(`${d}T00:00:00Z`) + n * 86_400_000).toISOString().slice(0, 10);

/**
 * SM2-R4-01 · CONTRATOS §7.9: Idempotency-Key de um envio de formulário. Gerada uma vez ao abrir o formulário e mantida
 * enquanto ele está aberto: clique duplo ou repetição após falha de rede mandam o mesmo valor e o servidor devolve o
 * documento já criado. Resposta de erro libera a chave no servidor, então corrigir e reenviar funciona.
 */
export const novaChave = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
